import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Link as MuiLink,
  Typography,
} from "@mui/material";
import React from "react";
import DateTimeUtils from "src/Shared/Tools/DateTimeUtils";
import { IImperansSubmission } from "../../Interfaces/IImperansSubmission";
import {
  groupIssuesByStep,
  issueLine,
  KEPT_IN_CRM_TITLE,
  showsKeptInCrm,
  STILL_CHECKING_MESSAGE,
  submissionSeverity,
  submissionTitle,
} from "./OnboardingSubmission.rules";

type Props = {
  submission: IImperansSubmission | null;
  /** True while a send or a retry is in flight: "Still checking with Imperans" (story 6339). */
  busy?: boolean;
  onGoToStep?: (step: number) => void;
  onRetry?: () => void;
};

export const TRY_AGAIN_LABEL = "Try again";

/**
 * Every possible Imperans answer, shown as itself (Epic 6303, Feature CRM1, story 6339): created
 * with its details and links, rejected with every error grouped by wizard step and an Edit link
 * (story 6340), refused for a configuration reason, failed or timed out with Try again, and "Still
 * checking" while a send is in flight. Imperans's messages appear verbatim with their check ids.
 */
const ImperansSubmissionResult: React.FC<Props> = ({ submission, busy, onGoToStep, onRetry }) => {
  if (busy) {
    return (
      <Alert severity="info" sx={{ mt: 2 }}>
        <AlertTitle>{STILL_CHECKING_MESSAGE}</AlertTitle>
        The onboarding has been sent. Imperans is creating the client; this can take up to a minute.
      </Alert>
    );
  }

  if (!submission) return null;

  const groups = groupIssuesByStep(submission.errors || []);

  return (
    <Alert
      severity={submissionSeverity(submission)}
      sx={{ mt: 2 }}
      action={
        submission.canRetry && onRetry ? (
          <Button color="inherit" size="small" onClick={onRetry}>
            {TRY_AGAIN_LABEL}
          </Button>
        ) : undefined
      }
    >
      <AlertTitle>{submissionTitle(submission)}</AlertTitle>

      {submission.isCreated ? (
        <>
          <Typography variant="body2">
            CompanyID {submission.imperansCompanyId}
            {submission.imperansCompanyCode ? ` (${submission.imperansCompanyCode})` : ""}
            {submission.createdAt
              ? `, created ${DateTimeUtils.dateToLocalTimeZone(submission.createdAt)}`
              : ""}
            {submission.createdBy ? ` by ${submission.createdBy}` : ""}.
          </Typography>
          {submission.ticket ? (
            <Typography variant="body2">
              Review ticket {submission.ticket.reference}
              {submission.ticket.url ? (
                <>
                  {" — "}
                  <MuiLink href={submission.ticket.url} target="_blank" rel="noreferrer">
                    open in Imperans
                  </MuiLink>
                </>
              ) : null}
              .
            </Typography>
          ) : (
            <Typography variant="body2">
              No review ticket: a regional client is set up without the Imperans setup team.
            </Typography>
          )}
          {submission.clientUrl && (
            <Typography variant="body2">
              <MuiLink href={submission.clientUrl} target="_blank" rel="noreferrer">
                Open the client in Imperans
              </MuiLink>
            </Typography>
          )}
          {submission.activatedAt && (
            <Typography variant="body2">
              Activated in Imperans {DateTimeUtils.dateToLocalTimeZone(submission.activatedAt)}
              {submission.activatedBy ? ` by ${submission.activatedBy}` : ""}.
            </Typography>
          )}
          {submission.warnings.length > 0 && (
            <Box sx={{ mt: 1 }}>
              <Typography variant="body2" fontWeight={600}>
                Imperans noted:
              </Typography>
              <Box component="ul" sx={{ m: 0, pl: 2 }}>
                {submission.warnings.map((warning, index) => (
                  <li key={`${warning.check}-${index}`}>
                    <Typography variant="body2">{issueLine(warning)}</Typography>
                  </li>
                ))}
              </Box>
            </Box>
          )}
          {showsKeptInCrm(submission) && (
            <Box sx={{ mt: 1 }}>
              <Typography variant="body2" fontWeight={600}>
                {KEPT_IN_CRM_TITLE}
              </Typography>
              <Box component="ul" sx={{ m: 0, pl: 2 }}>
                {submission.keptInCrm.map((line) => (
                  <li key={line}>
                    <Typography variant="body2">{line}</Typography>
                  </li>
                ))}
              </Box>
            </Box>
          )}
        </>
      ) : (
        <>
          {submission.message && <Typography variant="body2">{submission.message}</Typography>}
          {groups.map((group) => (
            <Box key={`${group.step}`} sx={{ mt: 1 }}>
              <Typography variant="body2" fontWeight={600}>
                {group.title}
                {group.step !== null && onGoToStep && submission.isEditableAgain ? (
                  <>
                    {" "}
                    <MuiLink
                      component="button"
                      type="button"
                      underline="hover"
                      onClick={() => onGoToStep(group.step!)}
                    >
                      Edit
                    </MuiLink>
                  </>
                ) : null}
              </Typography>
              <Box component="ul" sx={{ m: 0, pl: 2 }}>
                {group.issues.map((issue, index) => (
                  <li key={`${issue.check}-${issue.path}-${index}`}>
                    <Typography variant="body2">{issueLine(issue)}</Typography>
                  </li>
                ))}
              </Box>
            </Box>
          ))}
          {submission.attempt > 1 && (
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
              Sent {submission.attempt} times under the same key.
            </Typography>
          )}
        </>
      )}
    </Alert>
  );
};

export default ImperansSubmissionResult;
